import type { SiteAnnouncement } from '@lambda/shared/types';

const CACHE_KEY = 'bhg-public-site-announcements-v1';

/** Trust cached announcements briefly so the banner can paint before the network returns. */
export const PUBLIC_SITE_ANNOUNCEMENTS_CACHE_MAX_AGE_MS = 5 * 60 * 1000;

interface CachedPublicSiteAnnouncements {
  announcements: SiteAnnouncement[];
  fetchedAt: number;
}

let memoryCache: CachedPublicSiteAnnouncements | null = null;

function isFresh(fetchedAt: number): boolean {
  return Date.now() - fetchedAt <= PUBLIC_SITE_ANNOUNCEMENTS_CACHE_MAX_AGE_MS;
}

export function readPublicSiteAnnouncementsCache(): SiteAnnouncement[] | null {
  if (memoryCache && isFresh(memoryCache.fetchedAt)) {
    return memoryCache.announcements;
  }

  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedPublicSiteAnnouncements;
    if (!Array.isArray(parsed?.announcements) || typeof parsed.fetchedAt !== 'number') return null;
    if (!isFresh(parsed.fetchedAt)) {
      sessionStorage.removeItem(CACHE_KEY);
      memoryCache = null;
      return null;
    }
    memoryCache = parsed;
    return parsed.announcements;
  } catch {
    return null;
  }
}

export function writePublicSiteAnnouncementsCache(announcements: SiteAnnouncement[]): void {
  const payload: CachedPublicSiteAnnouncements = {
    announcements,
    fetchedAt: Date.now(),
  };
  memoryCache = payload;
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Private mode / quota — ignore
  }
}

